/**
 * UnknownArtifactRenderer — fallback renderer for `unknown` artifacts (or
 * any future `ArtifactType` the dispatcher doesn't know how to render).
 *
 * Shows a file-chip-style summary: a generic file icon, a description (or
 * a sensible fallback string), and small metadata rows for `mime_type` and
 * `file_path` (truncated) when present.
 *
 * Default actions when `artifact.actions` is empty:
 *   - download (id="download") — only when `payload.file_path` is set;
 *     without a backing file there's nothing to download, so we ship no
 *     default actions and the frame's action area stays empty.
 *
 * Spec: .workspace-spec/WORKSPACE_CONTRACT.md
 */
import { useMemo } from "react"

import type { Artifact, ArtifactAction, UnknownPayload } from "@/api/models"

import { File } from "@/components/icons"

import { ArtifactFrame } from "./ArtifactFrame"

function buildDefaultActions(payload: UnknownPayload): ArtifactAction[] {
  const actions: ArtifactAction[] = []
  if (payload.file_path) {
    actions.push({ id: "download", label: "Download", icon: "download" })
  }
  return actions
}

export interface UnknownArtifactRendererProps {
  artifact: Artifact & { payload: UnknownPayload }
  versionChain?: Artifact[]
  onAction?: (actionId: string, artifact: Artifact) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
}

export function UnknownArtifactRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
}: UnknownArtifactRendererProps) {
  const framedArtifact = useMemo<Artifact>(() => {
    const hasActions = (artifact.actions ?? []).length > 0
    const actions = hasActions ? artifact.actions : buildDefaultActions(artifact.payload)
    return { ...artifact, actions }
  }, [artifact])

  const { payload } = artifact
  const description = payload.description?.trim() || "Unknown artifact"

  return (
    <ArtifactFrame
      artifact={framedArtifact}
      versionChain={versionChain}
      onAction={onAction}
      onSelectVersion={onSelectVersion}
      onShowHistory={onShowHistory}
      onViewTrace={onViewTrace}
    >
      <div className="flex items-start gap-3 py-1" data-testid="unknown-artifact-renderer-body">
        <div
          aria-hidden
          className="bg-muted/60 flex size-9 shrink-0 items-center justify-center rounded-md border"
          data-testid="unknown-artifact-icon"
        >
          <File className="text-muted-foreground size-5" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p
            className="text-sm leading-snug font-medium"
            data-testid="unknown-artifact-description"
          >
            {description}
          </p>
          {payload.mime_type ? (
            <p
              className="text-muted-foreground font-mono text-xs"
              data-testid="unknown-artifact-mime"
            >
              {payload.mime_type}
            </p>
          ) : null}
          {payload.file_path ? (
            <p
              className="text-muted-foreground truncate font-mono text-xs"
              data-testid="unknown-artifact-path"
              title={payload.file_path}
            >
              {payload.file_path}
            </p>
          ) : null}
        </div>
      </div>
    </ArtifactFrame>
  )
}
